import { AlertCircle, RotateCw, X } from "lucide-react";
import { TOUCH_SQUARE } from "@/components/buttonStyles";
import { fileTile } from "@/lib/fileUploads";
import type { FileUpload } from "@/lib/useFileUploads";

// A file's row wherever files go to PMS (BRK-S361's loss runs, BRK-S362's
// answers to a quote's subjectivities): its tile, and while it isn't on
// record, its progress and Stop, or why it isn't attached with Dismiss, and
// Retry when it failed rather than was refused (`useFileUploads`).

/** The icon-only controls on a file's row: a 44px square under a coarse
 *  pointer. */
export const FILE_ICON_BUTTON = `rounded-md p-2 text-brk-dim hover:bg-brk-bg hover:text-brk-text disabled:opacity-60 ${TOUCH_SQUARE}`;

/** A file's row, bordered; the caller adds its border colour. */
export const FILE_ROW =
  "flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-[10px] border py-2.5 pl-3 pr-1";

/** A file's tile: its kind in capitals. */
export function FileTile({
  filename,
  tone,
}: {
  filename: string;
  tone: "file" | "muted";
}) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-caption font-bold ${
        tone === "file"
          ? "bg-brk-danger/10 text-brk-danger"
          : "bg-brk-bg text-brk-dim"
      }`}
    >
      {fileTile(filename)}
    </span>
  );
}

/** A file not on record yet: on its way up, or not attached. */
export function PendingFileRow({
  upload,
  onStop,
  onRetry,
  onDismiss,
}: {
  upload: FileUpload;
  onStop: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const name = upload.file.name;
  if (upload.state === "uploading") {
    return (
      <li className={`${FILE_ROW} border-brk-border`}>
        <FileTile filename={name} tone="muted" />
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="truncate font-semibold" title={name}>
            {name}
          </span>
          <span
            role="progressbar"
            aria-label={`Uploading ${name}`}
            aria-valuenow={upload.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="block h-1.5 overflow-hidden rounded-full bg-brk-bg"
          >
            <span
              className="block h-full rounded-full bg-brk-accent transition-[width] motion-reduce:transition-none"
              style={{ width: `${String(upload.percent)}%` }}
            />
          </span>
          <span className="text-meta text-brk-dim">
            Uploading… {upload.percent}%
          </span>
        </span>
        <button
          type="button"
          aria-label={`Stop uploading ${name}`}
          onClick={onStop}
          className={FILE_ICON_BUTTON}
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </li>
    );
  }
  const failed = upload.state === "failed";
  return (
    <li className={`${FILE_ROW} border-brk-danger/40 bg-brk-danger/5`}>
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brk-danger/10 text-brk-danger"
      >
        <AlertCircle size={16} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold" title={name}>
          {name}
        </span>
        <span role="alert" className="text-meta text-brk-danger">
          {failed ? "Didn’t upload: " : "Not attached: "}
          {upload.message}
        </span>
      </span>
      {failed && (
        <button
          type="button"
          aria-label={`Retry ${name}`}
          onClick={onRetry}
          className={FILE_ICON_BUTTON}
        >
          <RotateCw aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        aria-label={`Dismiss ${name}`}
        onClick={onDismiss}
        className={FILE_ICON_BUTTON}
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </li>
  );
}
